// MF-568 — Card 'pat-band-stanine-grid' (design 1B): achievement bands (low at the top) by stanine 1-9. Each occupied
// cell shows n and the median score. Click a cell to list its students; shift/ctrl-click or an adjacent cell compares groups.
import { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, INK } from '../boardTheme'
import { resolveColors, useBoardStyle } from '../boardPalettes'
import { buttonProps, median, prepareClass } from './patShared'
import { GroupLine, Interpretation, Roster, StanineNote } from './PatParts'

const STANINES = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const cellKey = (band, st) => `${band}|${st}`

const PatBandStanineGridCard = ({ data }) => {
  const style = useBoardStyle()
  const [primary] = resolveColors(style, {}, 2)
  const cls = useMemo(() => prepareClass(data?.rows || []), [data])
  const [cells, setCells] = useState([]) // selected cell keys

  const grid = useMemo(() => {
    const m = new Map()
    for (const s of cls.students) {
      if (s.stanine === null) continue
      const k = cellKey(s.bandKey, Math.round(s.stanine))
      if (!m.has(k)) m.set(k, [])
      m.get(k).push(s)
    }
    return m
  }, [cls])
  const maxN = Math.max(1, ...[...grid.values()].map((v) => v.length))
  const placed = [...grid.values()].reduce((a, v) => a + v.length, 0)

  const adjacent = (k) => {
    const [b, st] = k.split('|')
    const bi = cls.bands.findIndex((x) => x.key === b)
    return cells.some((c) => {
      const [cb, cs] = c.split('|')
      return (
        Math.abs(cls.bands.findIndex((x) => x.key === cb) - bi) <= 1 && Math.abs(Number(cs) - Number(st)) <= 1
      )
    })
  }
  const onCell = (k, e) => {
    if (!grid.has(k)) return
    setCells((cur) => {
      if (cur.includes(k)) return cur.filter((c) => c !== k)
      const multi = e?.shiftKey || e?.ctrlKey || e?.metaKey
      return multi || (cur.length && adjacent(k)) ? [...cur, k] : [k]
    })
  }

  if (!cls.hasStanine) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
        <GroupLine n={cls.students.length} excluded={cls.excluded} />
        <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.ink, mt: 2 }}>
          The band by stanine grid is not available yet.
        </Typography>
        <StanineNote />
        <Interpretation />
      </Box>
    )
  }

  const group = cells.flatMap((k) => grid.get(k) || []).sort((a, b) => b.score - a.score)
  const label =
    cells.length === 1
      ? `Band ${cells[0].split('|')[0]} · stanine ${cells[0].split('|')[1]}`
      : `${cells.length} cells compared`

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
      <GroupLine n={placed} excluded={cls.excluded} noStanine={cls.noStanine} />
      <Box sx={{ overflowX: 'auto', mt: 1 }}>
        <Box
          role='grid'
          aria-label='Achievement band by stanine'
          sx={{
            display: 'grid',
            gridTemplateColumns: '84px repeat(9, minmax(44px, 1fr))',
            gap: '4px',
            minWidth: 520
          }}
        >
          <Box />
          {STANINES.map((st) => (
            <Typography
              key={st}
              role='columnheader'
              sx={{ fontSize: 11, fontWeight: 600, textAlign: 'center', color: SUMMARY_PALETTE.muted }}
            >
              Stanine {st}
            </Typography>
          ))}
          {cls.bands.map((b) => (
            <Box key={b.key} sx={{ display: 'contents' }} role='row'>
              <Box
                role='rowheader'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 11,
                  fontWeight: 600,
                  color: SUMMARY_PALETTE.ink
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                {b.key}
              </Box>
              {STANINES.map((st) => {
                const k = cellKey(b.key, st)
                const list = grid.get(k)
                const on = cells.includes(k)
                if (!list) {
                  return (
                    <Box
                      key={k}
                      role='gridcell'
                      aria-label={`Band ${b.key}, stanine ${st}: no students`}
                      sx={{
                        height: 46,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: INK.muted,
                        borderRadius: '6px',
                        border: `1px dashed ${SUMMARY_PALETTE.grid}`
                      }}
                    >
                      -
                    </Box>
                  )
                }
                const t = list.length / maxN
                return (
                  <Box
                    key={k}
                    role='gridcell'
                    aria-label={`Band ${b.key}, stanine ${st}: n ${list.length}, median score ${median(list.map((s) => s.score))}`}
                    {...buttonProps((e) => onCell(k, e), on)}
                    sx={{
                      height: 46,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1.2,
                      fontSize: 11,
                      bgcolor: `${primary}${Math.round(30 + t * 90)
                        .toString(16)
                        .padStart(2, '0')}`,
                      border: `2px solid ${on ? INK.primary : 'transparent'}`,
                      color: SUMMARY_PALETTE.ink
                    }}
                  >
                    <b>n={list.length}</b>
                    <span>median {median(list.map((s) => s.score))}</span>
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.muted, mt: 1 }}>
        Band and stanine both derive from the score for one year level: this is a cross-tabulation, not two
        independent abilities. Shift/ctrl-click, or pick a neighbouring cell, to compare groups.
      </Typography>
      {cls.yearLevels.length > 1 ? (
        <Typography role='note' sx={{ fontSize: 12, color: SUMMARY_PALETTE.bad, mt: 0.5 }}>
          This group mixes {cls.yearLevels.length} year levels ({cls.yearLevels.join(', ')}). Stanines are
          read against each year level&apos;s norm.
        </Typography>
      ) : null}
      {cls.noStanine > 0 ? <StanineNote /> : null}
      <Roster students={group} label={label} onClear={() => setCells([])} />
      <Interpretation />
    </Box>
  )
}

export default PatBandStanineGridCard
